import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, useDisclosure } from '@heroui/react';
import { PageHeader } from '@/components/common/PageHeader';
import { Pagination } from '@/components/common/Pagination';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { SearchInput } from '@/components/common/SearchInput';
import { useTableQueryState } from '@/hooks/useTableQueryState';
import { useDomainsList, useDeleteDomain, useVerifyDomain } from '@/features/domains/hooks';
import { DomainsTable } from '@/features/domains/components/DomainsTable';
import { DomainFormModal } from '@/features/domains/components/DomainFormModal';
import type { Domain } from '@/features/domains/types';
import { PlusIcon } from '@/icons';
import { useOpenOnNewParam } from '@/hooks/useOpenOnNewParam';
import { usePermission } from '@/hooks/usePermission';

/** Domains list: search, sort, paginate, add, verify, delete. */
export function DomainsPage() {
  const navigate = useNavigate();
  const { state, setPage, setPageSize, setSearch, toggleSort } = useTableQueryState();

  const { data, isLoading, isError, error, refetch } = useDomainsList({
    page: state.page, pageSize: state.pageSize, q: state.q || undefined, sort: state.sort || undefined,
  });

  const form = useDisclosure();
  const del = useDeleteDomain();
  // Create/delete need zone:write at the gateway; hide the controls for roles without it.
  const canWrite = usePermission().can('zone:write');
  useOpenOnNewParam(() => { if (canWrite) form.onOpen(); });
  const [pendingVerify, setPendingVerify] = useState<Domain | null>(null);
  const verify = useVerifyDomain(pendingVerify?.id ?? '');
  const [toDelete, setToDelete] = useState<Domain | null>(null);

  return (
    <div>
      <PageHeader
        title="Domains"
        description="Add and verify mail domains, and manage their DNS."
        actions={canWrite ? <Button color="primary" startContent={<PlusIcon className="h-4 w-4" />} onPress={form.onOpen}>Add domain</Button> : undefined}
      />
      <div className="rounded-xl border border-border bg-surface">
        <div className="border-b border-border p-4">
          <SearchInput value={state.q} onSearch={setSearch} placeholder="Search domains…" className="sm:max-w-xs" ariaLabel="Search domains" />
        </div>
        <DomainsTable
          domains={data?.data ?? []} isLoading={isLoading} isError={isError} error={error} onRetry={() => refetch()} sort={state.sort} onSort={toggleSort}
          onVerify={(d) => { setPendingVerify(d); setTimeout(() => verify.mutate(), 0); }}
          onDelete={canWrite ? setToDelete : undefined}
        />
        <Pagination page={state.page} pageSize={state.pageSize} total={data?.total} onPageChange={setPage} onPageSizeChange={setPageSize} />
      </div>

      <DomainFormModal isOpen={form.isOpen} onClose={form.onClose} onCreated={(id) => navigate(`/domains/${id}`)} />
      <ConfirmDialog
        isOpen={!!toDelete} onClose={() => setToDelete(null)}
        onConfirm={async () => { if (toDelete) await del.mutateAsync(toDelete.id); setToDelete(null); }}
        title="Delete domain?" body={`Delete ${toDelete?.domain} and all its mail objects? This cannot be undone.`}
        confirmLabel="Delete" tone="danger" isLoading={del.isPending}
      />
    </div>
  );
}
